import { catchError, of, take } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';
import { filter, identity, map, pickBy, range } from 'lodash';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';

import { LandingPageTypeEnum } from '@sifca-monorepo/ecommerce-generator';
import { LandingPagesWithoutTranslationType } from '@sifca-monorepo/ecommerce-generator';

import { LandingPagesService, SeoService, SharedService } from '../../services';

@Component({
  template: '',
})
export class BaseProjectListComponent implements OnInit {
  seo: any;
  currentPage = 1;
  numberOfPages = 3;
  startingPages = 0;
  pageRange: number[];
  totalOfPages: number;
  pages: LandingPagesWithoutTranslationType[];
  landingPages: LandingPagesWithoutTranslationType[];
  posId: string = '';
  constructor(
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected landingPagesService: LandingPagesService,
    @Inject(PLATFORM_ID) protected platformId: Object,
    @Inject(DOCUMENT) protected document: Document
  ) {
    this.sharedService.posId$.subscribe((posId) => {
      this.posId = posId;
    });
    this.landingPagesService.limit = 10;
    this.landingPagesService
      .findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT, LandingPageTypeEnum.PROJECTS])
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
        this.pages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECTS);
        this.seoService
          .getPagesSEOJSON(this.pages[0].id)
          .pipe(
            take(1),
            catchError(() => {
              return of(null);
            }),
          )
          .subscribe((res) => {
            this.seo = res;
            if (this.seo?.title) {
              this.titleTagService.setTitle(this.seo?.title);
            }
            this.sharedService.tags$ = [
              ...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))),
              { charset: 'UTF-8' },
            ];
              this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
                const link = this.renderer.createElement('link');
                if (seoLink.sizes) {
                  link.sizes = seoLink.sizes;
                }
                if (seoLink.href) {
                  link.href = seoLink.href;
                }
                if (seoLink.rel) {
                  link.rel = seoLink.rel;
                }
                this.renderer.appendChild(this.document.head, link);
                return link;
              });
          });
        this.changeDetectorRef.markForCheck();
      });
    this.landingPagesService.limit = 9;
  }

  ngOnInit(): void {
    this.landingPagesService.page = 0;
    this.landingPagesService.landingPages$.subscribe((pages) => {
      if (pages) {
        this.totalOfPages = pages.count / this.landingPagesService.limit;
        if (this.numberOfPages < this.totalOfPages) {
          this.pageRange = range(this.startingPages, this.numberOfPages);
        } else {
          this.pageRange = range(this.startingPages, this.totalOfPages);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  getPage(page: number) {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.landingPagesService.page = page;
    this.currentPage = page + 1;
    if (this.currentPage === this.pageRange.length && this.totalOfPages > this.numberOfPages + 3) {
      this.numberOfPages += 3;
      this.startingPages += 3;
      this.pageRange = range(this.startingPages, this.numberOfPages);
    } else {
      this.pageRange = range(this.startingPages, this.totalOfPages);
    }
    this.landingPagesService
      .findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT])
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
      });
  }

  getLastPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.landingPagesService.page = this.totalOfPages - 1;
    this.pageRange = range(this.totalOfPages - 1, this.totalOfPages);
    this.currentPage = this.totalOfPages;
    this.landingPagesService.findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT]).subscribe((res) => {
      this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
    });
  }

  getFirstPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.currentPage = 1;
    this.landingPagesService.page = 0;
    this.pageRange = range(0, 2);
    this.landingPagesService.findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT]).subscribe((res) => {
      this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
    });
  }

  loadPrevPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.currentPage -= 1;
    this.landingPagesService.page -= 1;
    this.pageRange = range(0, 2);
    this.landingPagesService.findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT]).subscribe((res) => {
      this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
    });
  }

  loadNextPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.numberOfPages++;
    this.currentPage += 1;
    this.landingPagesService.page += 1;
    this.pageRange = range(0, 2);
    if (this.currentPage > this.pageRange.length) {
      this.pageRange = range(0, this.numberOfPages);
    }
    this.landingPagesService.findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.PROJECT]).subscribe((res) => {
      this.landingPages = filter(res.objects, (page) => page.pageType === LandingPageTypeEnum.PROJECT);
    });
  }
}
